import AttachmentTable from './AttachmentsTable';
import { SideSheet, SideSheetHeader } from '../../../layouts';
import { formatDayJsDate } from '../../../utils/dateTimeUtils';
import { convertDateToDayJs } from '../../../utils/dateTimeUtils';
import { getFormattedAmount } from '../../../utils/form';
import { formatNumber } from '../../../utils/form';

interface PerKmLineItemDetailsProps {
  isOpen: boolean;
  onClose: () => void;
  lineItemData: any;
  attachments: any[];
  travelRequestId: number;
  formConfig: any;
  headerChildren?: React.ReactNode;
}

export function GridDataPoint({
  config,
}: {
  config: { label: string; value: React.ReactNode };
}) {
  const { label, value } = config;
  return (
    <div className="flex flex-col gap-y-1">
      <div className="font-semibold text-text-200 text-xs">{label}</div>
      <div className="font-normal text-text-400 text-xs">{value}</div>
    </div>
  );
}

const PerKmLineItemDetails = ({
  isOpen,
  onClose,
  lineItemData,
  attachments,
  travelRequestId,
  formConfig,
  headerChildren,
}: PerKmLineItemDetailsProps) => {
  if (!isOpen) return null;
  const fields = formConfig?.perKmForm?.fields;

  return (
    <SideSheet
      closeOnClickOutside={true}
      isOpen={isOpen}
              addOnClass="w-full max-w-none md:!w-[50vw]"
      onClose={onClose}
      contentChildren={
        <div className="flex flex-col gap-4 p-3">
          {lineItemData && (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {fields?.modeOfTransport?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.modeOfTransport?.label ||
                        fields?.modeOfTransport?.defaultLabel ||
                        'Mode of Transport',
                      value:
                        lineItemData.modeOfTransport?.label ||
                        lineItemData.travelModeofTransport?.name ||
                        '-',
                    }}
                  />
                )}
                {fields?.fromDate?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.fromDate?.label ||
                        fields?.fromDate?.defaultLabel ||
                        'Date',
                      value: lineItemData.fromDate
                        ? formatDayJsDate(
                            convertDateToDayJs(lineItemData.fromDate),
                          )
                        : '-',
                    }}
                  />
                )}
                {fields?.toDate?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.toDate?.label ||
                        fields?.toDate?.defaultLabel ||
                        'To Date',
                      value: lineItemData.toDate
                        ? formatDayJsDate(
                            convertDateToDayJs(lineItemData.toDate),
                          )
                        : '-',
                    }}
                  />
                )}
                {fields?.source?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.source?.label ||
                        fields?.source?.defaultLabel ||
                        'Source',
                      value: lineItemData.source || '-',
                    }}
                  />
                )}
                {fields?.destination?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.destination?.label ||
                        fields?.destination?.defaultLabel ||
                        'Destination',
                      value: lineItemData.destination || '-',
                    }}
                  />
                )}
                {fields?.distanceInKm?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.distanceInKm?.label ||
                        fields?.distanceInKm?.defaultLabel ||
                        'Distance In Km',
                      value: lineItemData.distanceInKm
                        ? formatNumber(lineItemData.distanceInKm)
                        : '-',
                    }}
                  />
                )}
                {fields?.fromMeter?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.fromMeter?.label ||
                        fields?.fromMeter?.defaultLabel ||
                        'Meter (From)',
                      value: lineItemData.fromMeter
                        ? formatNumber(lineItemData.fromMeter)
                        : '-',
                    }}
                  />
                )}
                {fields?.toMeter?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.toMeter?.label ||
                        fields?.toMeter?.defaultLabel ||
                        'Meter (To)',
                      value: lineItemData.toMeter
                        ? formatNumber(lineItemData.toMeter)
                        : '-',
                    }}
                  />
                )}
                {fields?.ratePerKm?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.ratePerKm?.label ||
                        fields?.ratePerKm?.defaultLabel ||
                        'Rate Per Km',
                      value: lineItemData.ratePerKm
                        ? getFormattedAmount(lineItemData.ratePerKm)
                        : '-',
                    }}
                  />
                )}
                {fields?.total?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.total?.label ||
                        fields?.total?.defaultLabel ||
                        'Amount',
                      value: lineItemData.total
                        ? getFormattedAmount(lineItemData.total)
                        : '-',
                    }}
                  />
                )}
                {fields?.approvedAmount?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.approvedAmount?.label ||
                        fields?.approvedAmount?.defaultLabel ||
                        'Approved Amount',
                      value: lineItemData.approvedAmount
                        ? getFormattedAmount(lineItemData.approvedAmount)
                        : '-',
                    }}
                  />
                )}
                {fields?.remarks?.visible && (
                  <GridDataPoint
                    config={{
                      label:
                        fields?.remarks?.label ||
                        fields?.remarks?.defaultLabel ||
                        'Remarks',
                      value: lineItemData.remarks || '-',
                    }}
                  />
                )}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <h2 className="text-md">Attachments</h2>
            <AttachmentTable
              attachments={attachments || []}
              travelRequestId={travelRequestId}
            />
          </div>
        </div>
      }
      headerChildren={
        <div className="border-b border-gray-100">
          {headerChildren || (
            <SideSheetHeader title="Per Km Details" onCloseClick={onClose} />
          )}
        </div>
      }
      footerChildren={<></>}
    />
  );
};

export default PerKmLineItemDetails;
